import React, { useEffect, useMemo, useState } from "react";

const CATEGORY_LABELS = {
  CONSEIL_FORMATION: "CONSEIL ET FORMATION",
  PREPA_GRANDE_CROISIERE: "PRÉPARATION GRANDE CROISIÈRE",
  COQUE_PONT: "PRÉPARATION COQUE & PONT",
  METALLERIE: "TRAVAUX DE MÉTALLERIE",
  GREEMENT_VOILES: "GRÉEMENT & VOILES",
  NETTOYAGE_CONCIERGERIE: "NETTOYAGE & CONCIERGERIE",
};

function getAllCategories(item) {
  const cats = Array.isArray(item?.categories) ? item.categories : [];
  if (cats.length > 0) return cats;

  // compat ancien champ
  if (item?.category) {
    const one = String(item.category).trim();
    return one ? [one] : [];
  }
  return [];
}

function getImages(item) {
  // ✅ on accepte plusieurs formats (en attendant l’upload multi)
  // 1) images[] (galerie)
  // 2) coverImage (image principale)
  // 3) cover (ancien champ)
  const imgs = Array.isArray(item?.images) ? item.images : [];

  const normalized = imgs
    .map((im) => ({
      url: im?.url || "",
      alt: im?.alt || item?.title || "",
    }))
    .filter((x) => x.url);

  const coverUrl = item?.coverImage?.url || item?.cover || "";
  if (coverUrl && !normalized.some((x) => x.url === coverUrl)) {
    normalized.unshift({ url: coverUrl, alt: item?.coverImage?.alt || item?.title || "" });
  }

  return normalized;
}

export default function RealisationModal({ item, onClose }) {
  const images = useMemo(() => getImages(item), [item]);
  const cats = useMemo(() => getAllCategories(item), [item]);

  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [item?._id]);

  // ESC + lock scroll
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") setIndex((i) => Math.max(0, i - 1));
      if (e.key === "ArrowRight") setIndex((i) => Math.min(images.length - 1, i + 1));
    };
    document.addEventListener("keydown", onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, images.length]);

  const current = images[index];

  // "commentaires" : si tu n’as pas de champ dédié, on affiche description
  const comments =
    item?.comments ??
    item?.commentaires ??
    item?.notes ??
    ""; // si plus tard tu ajoutes un champ

  return (
    <div className="real-modal__backdrop" onMouseDown={onClose}>
      <div className="real-modal" onMouseDown={(e) => e.stopPropagation()}>
        <button className="real-modal__close" type="button" onClick={onClose} aria-label="Fermer">
          ✕
        </button>

        <div className="real-modal__grid">
          {/* MEDIA */}
          <div className="real-modal__media">
            {current?.url ? (
              <>
                <img className="real-modal__img" src={current.url} alt={current.alt || item?.title || ""} />
                {images.length > 1 && (
                  <div className="real-modal__nav">
                    <button
                      type="button"
                      className="real-btn real-btnSmall"
                      onClick={() => setIndex((i) => Math.max(0, i - 1))}
                      disabled={index === 0}
                    >
                      ←
                    </button>
                    <div className="real-modal__count">
                      {index + 1} / {images.length}
                    </div>
                    <button
                      type="button"
                      className="real-btn real-btnSmall"
                      onClick={() => setIndex((i) => Math.min(images.length - 1, i + 1))}
                      disabled={index === images.length - 1}
                    >
                      →
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="real-modal__imgEmpty">Aucune image</div>
            )}

            {images.length > 1 && (
              <div className="real-modal__thumbs">
                {images.map((im, i) => (
                  <button
                    key={`${im.url}-${i}`}
                    type="button"
                    className={`real-modal__thumb ${i === index ? "is-active" : ""}`}
                    onClick={() => setIndex(i)}
                    aria-label={`Image ${i + 1}`}
                  >
                    <img src={im.url} alt="" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* CONTENT */}
          <div className="real-modal__content">
            <div className="real-modal__top">
              <h2 className="real-modal__title">{item?.title}</h2>
              <div className="real-modal__meta">
                {item?.year ? <span>{item.year}</span> : null}
                {item?.link ? (
                  <a className="real-modal__link" href={item.link} target="_blank" rel="noreferrer">
                    Ouvrir le lien ↗
                  </a>
                ) : null}
              </div>
            </div>

            {/* catégories */}
            <div className="real-modal__section">
              <div className="real-modal__h">Catégories</div>
              {cats.length ? (
                <div className="real-modal__chips">
                  {cats.map((k, i) => (
                    <span key={`${k}-${i}`} className="real-chip">
                      {CATEGORY_LABELS[k] || k}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="real-muted">Aucune catégorie</div>
              )}
            </div>

            {/* description */}
            <div className="real-modal__section">
              <div className="real-modal__h">Description</div>
              <p className="real-modal__p">{item?.description || "—"}</p>
            </div>

            {/* commentaires */}
            <div className="real-modal__section">
              <div className="real-modal__h">Commentaires</div>
              {Array.isArray(comments) ? (
                comments.length ? (
                  <ul className="real-modal__list">
                    {comments.map((c, i) => (
                      <li key={i}>{String(c)}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="real-muted">Aucun commentaire</div>
                )
              ) : (
                <p className="real-modal__p">{String(comments || "Aucun commentaire")}</p>
              )}
            </div>

            {/* tags */}
            <div className="real-modal__section">
              <div className="real-modal__h">Tags</div>
              {item?.tags?.length ? (
                <div className="real-modal__chips">
                  {item.tags.map((t, i) => (
                    <span key={`${t}-${i}`} className="real-chip">
                      #{t}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="real-muted">Aucun tag</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
